// frontend/src/components/chat/patient-panel/ProblemCard.tsx
import { FC } from 'react'
import { GripVertical, Pin, ChevronRight, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Button } from '../../ui/button'
import { cn } from '../../../lib/utils'
import { EnhancedProblem } from './types'

interface ProblemCardProps {
  problem: EnhancedProblem
  onPin: () => void
  onClick?: () => void
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>
}

const statusStyles: Record<string, string> = {
  worsening: 'text-clinical-coral',
  stable: 'text-muted-foreground',
  improving: 'text-clinical-teal',
  resolved: 'text-clinical-teal opacity-60',
  unknown: 'text-muted-foreground',
}

const StatusIcon: FC<{ status: string }> = ({ status }) => {
  const iconClass = 'h-3.5 w-3.5'
  switch (status) {
    case 'worsening':
      return <TrendingUp className={cn(iconClass, 'text-clinical-coral')} />
    case 'improving':
      return <TrendingDown className={cn(iconClass, 'text-clinical-teal')} />
    case 'stable':
    default:
      return <Minus className={cn(iconClass, 'text-muted-foreground')} />
  }
}

export const ProblemCard: FC<ProblemCardProps> = ({
  problem,
  onPin,
  onClick,
  dragHandleProps,
}) => {
  const { extraction, priority, status, isPinned } = problem

  return (
    <div
      className={cn(
        'group flex items-center gap-2 p-2 rounded-lg border transition-colors',
        'hover:bg-muted/50 cursor-pointer',
        isPinned && 'border-clinical-teal/30 bg-clinical-teal/5'
      )}
      onClick={onClick}
    >
      {/* Drag handle */}
      <div
        {...dragHandleProps}
        className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Priority badge */}
      <span className={cn(
        'flex items-center justify-center w-5 h-5 rounded text-xs font-medium',
        priority <= 2 ? 'bg-clinical-coral/15 text-clinical-coral' : 'bg-muted text-muted-foreground'
      )}>
        {priority}
      </span>

      {/* Problem content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <StatusIcon status={status} />
          <span className={cn('text-sm font-medium truncate', statusStyles[status])}>
            {extraction.content}
          </span>
        </div>
        {extraction.next_action && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            Next: {extraction.next_action}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className={cn('h-6 w-6 p-0', isPinned && 'text-clinical-teal')}
          onClick={(e) => {
            e.stopPropagation()
            onPin()
          }}
        >
          <Pin className={cn('h-3.5 w-3.5', isPinned && 'fill-current')} />
        </Button>
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  )
}
